import React, {useEffect, useState} from 'react';
import {Typography} from 'src/styles';
import {useStyle} from 'src/providers/style';
import {FlatList, Text, TouchableOpacity, View} from 'react-native';
import NoResults from 'src/common-components/noResults';
import {Separator} from 'src/common-components/atoms';
import AuthorizationProgressItem from 'src/modules/practice-management/components/widgets/authorization-progress-item';
import {getAuthorizationStatusText} from 'src/common-utils/authorization-expiration';
import {Patient} from 'src/models';
import ListItemSeparator from 'src/common-components/separator';
import moment from 'moment';

const AuthorizationProgress = ({patients}: any) => {
  const styles = useStyle();
  const [daysAhead, setDaysAhead] = useState<number>(90);
  const [filteredPatients, setFilteredPatients] = useState<
    {patient: Patient; statusText: string}[]
  >([]);

  useEffect(() => {
    const filterPatients = async () => {
      const patientChecks = await Promise.all(
        patients.map(async (patient: Patient) => {
          const activeAuthorizations = await patient.activeAuthorizations;
          const statusText = getAuthorizationStatusText(
            activeAuthorizations,
            daysAhead,
          );

          let soonestDate: moment.Moment | null = null;
          if (activeAuthorizations && activeAuthorizations.length > 0) {
            const dates = activeAuthorizations
              .map(auth => moment(auth?.endDate))
              .filter(date => date.isValid());
            if (dates.length > 0) {
              soonestDate = dates.reduce((earliest, current) =>
                current.isBefore(earliest) ? current : earliest,
              );
            }
          }

          return {patient, statusText, soonestDate};
        }),
      );

      const filtered = patientChecks.filter(
        p =>
          !!p.statusText &&
          (daysAhead === 0
            ? true
            : !p.statusText.includes('No Active Authorizations')),
      );

      filtered.sort((a, b) => {
        if (!a.soonestDate && !b.soonestDate) {
          return 0;
        }
        if (!a.soonestDate) {
          return 1;
        }
        if (!b.soonestDate) {
          return -1;
        }
        return b.soonestDate.valueOf() - a.soonestDate.valueOf();
      });

      setFilteredPatients(filtered);
    };

    filterPatients();
  }, [patients, daysAhead]);

  return (
    <>
      <View style={[styles.flex, styles.card, styles.padding]}>
        <Text style={[Typography.P2_BOLD, styles.paddingMBottom]}>
          Expiring Active Client Authorizations
        </Text>
        <View
          style={[
            styles.container,
            styles.paddingLBottom,
            styles.justifySpaceBetween,
          ]}>
          <View style={[styles.row]}>
            <TouchableOpacity onPress={() => setDaysAhead(0)}>
              <View style={[daysAhead === 0 ? [styles.tabActive] : {}]}>
                <Text
                  style={[
                    styles.paddingSMBottom,
                    Typography.P3_MEDIUM,
                    styles.textColorError,
                  ]}>
                  Expired
                </Text>
              </View>
            </TouchableOpacity>
            <Separator width={20} />

            <TouchableOpacity onPress={() => setDaysAhead(30)}>
              <View style={[daysAhead === 30 ? styles.tabActive : {}]}>
                <Text style={[styles.paddingSMBottom, Typography.P3_MEDIUM]}>
                  30 Days
                </Text>
              </View>
            </TouchableOpacity>
            <Separator width={20} />

            <TouchableOpacity onPress={() => setDaysAhead(60)}>
              <View style={[daysAhead === 60 ? styles.tabActive : {}]}>
                <Text style={[styles.paddingSMBottom, Typography.P3_MEDIUM]}>
                  60 Days
                </Text>
              </View>
            </TouchableOpacity>
            <Separator width={20} />

            <TouchableOpacity onPress={() => setDaysAhead(90)}>
              <View style={[daysAhead === 90 ? styles.tabActive : {}]}>
                <Text style={[styles.paddingSMBottom, Typography.P3_MEDIUM]}>
                  90 Days
                </Text>
              </View>
            </TouchableOpacity>
            <Separator width={20} />
          </View>
        </View>
        <View style={[styles.flex, styles.column, styles.justifySpaceBetween]}>
          <View style={[styles.flex, styles.row, styles.paddingBottom]}>
            <Text
              style={[styles.flex, styles.paddingSMBottom, Typography.P3_BOLD]}>
              Client
            </Text>
            <Text
              style={[styles.flex, styles.paddingSMBottom, Typography.P3_BOLD]}>
              Expiration
            </Text>
          </View>
          {filteredPatients?.length > 0 ? (
            <FlatList
              keyExtractor={item => item.patient?.id}
              data={filteredPatients}
              extraData={daysAhead}
              ItemSeparatorComponent={ListItemSeparator}
              renderItem={({item}) => (
                <AuthorizationProgressItem
                  patient={item.patient}
                  statusText={item.statusText}
                />
              )}
            />
          ) : (
            <NoResults
              iconName={'account-check'}
              message={'No Expiring Authorizations Found'}
            />
          )}
        </View>
      </View>
    </>
  );
};

export default AuthorizationProgress;
